/**
 * Indian Pincode Coordinates & Haversine Distance Calculator
 * Calculates accurate approximate road distance between any two Indian pincodes.
 */

interface Coordinates {
  lat: number;
  lon: number;
  name: string;
}

// ─── 3-digit and 2-digit Pincode Region Coordinate Map for India ─────────────
const PINCODE_COORDINATES: Record<string, Coordinates> = {
  // Delhi & NCR
  '110': { lat: 28.6139, lon: 77.2090, name: 'Delhi' },
  '11':  { lat: 28.6139, lon: 77.2090, name: 'Delhi' },
  '121': { lat: 28.4089, lon: 77.3178, name: 'Faridabad, HR' },
  '122': { lat: 28.4595, lon: 77.0266, name: 'Gurugram, HR' },
  '201': { lat: 28.5355, lon: 77.3910, name: 'Noida / Ghaziabad, UP' },

  // Haryana (12 - 13)
  '12':  { lat: 29.0588, lon: 76.0856, name: 'Haryana' },
  '131': { lat: 28.9931, lon: 77.0151, name: 'Sonipat, HR' },
  '132': { lat: 29.6857, lon: 76.9905, name: 'Karnal / Panipat, HR' },
  '133': { lat: 30.3782, lon: 76.7767, name: 'Ambala, HR' },
  '134': { lat: 30.6942, lon: 76.8606, name: 'Panchkula, HR' },
  '13':  { lat: 29.5000, lon: 76.5000, name: 'Haryana' },

  // Punjab & Chandigarh (14 - 16)
  '160': { lat: 30.7333, lon: 76.7794, name: 'Chandigarh' },
  '141': { lat: 30.9010, lon: 75.8573, name: 'Ludhiana, PB' },
  '143': { lat: 31.6340, lon: 74.8723, name: 'Amritsar, PB' },
  '144': { lat: 31.3260, lon: 75.5762, name: 'Jalandhar, PB' },
  '14':  { lat: 31.1471, lon: 75.3412, name: 'Punjab' },
  '15':  { lat: 30.2110, lon: 74.9455, name: 'Punjab (Bathinda)' },
  '16':  { lat: 30.7333, lon: 76.7794, name: 'Chandigarh / Mohali' },

  // Himachal Pradesh (17)
  '171': { lat: 31.1048, lon: 77.1734, name: 'Shimla, HP' },
  '17':  { lat: 31.7500, lon: 77.1000, name: 'Himachal Pradesh' },

  // Jammu & Kashmir & Ladakh (18 - 19)
  '180': { lat: 32.7266, lon: 74.8570, name: 'Jammu, JK' },
  '190': { lat: 34.0837, lon: 74.7973, name: 'Srinagar, JK' },
  '18':  { lat: 32.7266, lon: 74.8570, name: 'Jammu' },
  '19':  { lat: 34.0837, lon: 74.7973, name: 'Kashmir / Ladakh' },

  // Uttar Pradesh (20 - 28)
  '208': { lat: 26.4499, lon: 80.3319, name: 'Kanpur, UP' },
  '226': { lat: 26.8467, lon: 80.9462, name: 'Lucknow, UP' },
  '221': { lat: 25.3176, lon: 82.9739, name: 'Varanasi, UP' },
  '211': { lat: 25.4358, lon: 81.8463, name: 'Prayagraj, UP' },
  '282': { lat: 27.1767, lon: 78.0081, name: 'Agra, UP' },
  '250': { lat: 28.9845, lon: 77.7064, name: 'Meerut, UP' },
  '20':  { lat: 27.8974, lon: 78.0880, name: 'Western UP' },
  '21':  { lat: 25.4358, lon: 81.8463, name: 'Allahabad Region, UP' },
  '22':  { lat: 26.8467, lon: 80.9462, name: 'Central UP' },
  '23':  { lat: 25.1500, lon: 82.5800, name: 'Mirzapur Region, UP' },
  '24':  { lat: 28.8386, lon: 78.7733, name: 'Bareilly / Moradabad, UP' },
  '25':  { lat: 28.9845, lon: 77.7064, name: 'Meerut Region, UP' },
  '27':  { lat: 26.7606, lon: 83.3732, name: 'Gorakhpur, UP' },
  '28':  { lat: 25.4484, lon: 78.5685, name: 'Jhansi / Bundelkhand, UP' },

  // Uttarakhand (248, 249, 263)
  '248': { lat: 30.3165, lon: 78.0322, name: 'Dehradun, UK' },
  '249': { lat: 29.9457, lon: 78.1642, name: 'Haridwar / Rishikesh, UK' },
  '263': { lat: 29.2183, lon: 79.5130, name: 'Nainital / Haldwani / Ramnagar, UK' },
  '26':  { lat: 29.5000, lon: 79.5000, name: 'Uttarakhand' },

  // Rajasthan (30 - 34)
  '302': { lat: 26.9124, lon: 75.7873, name: 'Jaipur, RJ' },
  '342': { lat: 26.2389, lon: 73.0243, name: 'Jodhpur, RJ' },
  '313': { lat: 24.5854, lon: 73.7125, name: 'Udaipur, RJ' },
  '324': { lat: 25.2138, lon: 75.8648, name: 'Kota, RJ' },
  '305': { lat: 26.4499, lon: 74.6399, name: 'Ajmer, RJ' },
  '301': { lat: 27.5530, lon: 76.6346, name: 'Alwar / Bhiwadi, RJ' },
  '30':  { lat: 26.9124, lon: 75.7873, name: 'Jaipur Region, RJ' },
  '31':  { lat: 24.8887, lon: 74.6269, name: 'Chittorgarh / Udaipur, RJ' },
  '32':  { lat: 25.2138, lon: 75.8648, name: 'Kota Region, RJ' },
  '33':  { lat: 28.0229, lon: 73.3119, name: 'Bikaner, RJ' },
  '34':  { lat: 26.2389, lon: 73.0243, name: 'Jodhpur Region, RJ' },

  // Gujarat & Daman/Diu (36 - 39)
  '380': { lat: 23.0225, lon: 72.5714, name: 'Ahmedabad, GJ' },
  '390': { lat: 22.3072, lon: 73.1812, name: 'Vadodara, GJ' },
  '395': { lat: 21.1702, lon: 72.8311, name: 'Surat, GJ' },
  '360': { lat: 22.3039, lon: 70.8022, name: 'Rajkot, GJ' },
  '396': { lat: 20.3893, lon: 72.9106, name: 'Vapi / Daman / Silvassa' },
  '36':  { lat: 22.0000, lon: 71.0000, name: 'Saurashtra, GJ' },
  '37':  { lat: 23.2420, lon: 69.6669, name: 'Kutch / Bhuj, GJ' },
  '38':  { lat: 23.0225, lon: 72.5714, name: 'North & Central Gujarat' },
  '39':  { lat: 21.1702, lon: 72.8311, name: 'South Gujarat' },

  // Maharashtra & Goa (40 - 44)
  '400': { lat: 19.0760, lon: 72.8777, name: 'Mumbai, MH' },
  '401': { lat: 19.4200, lon: 72.8200, name: 'Thane / Palghar, MH' },
  '410': { lat: 18.7500, lon: 73.4000, name: 'Raigad / Lonavala, MH' },
  '411': { lat: 18.5204, lon: 73.8567, name: 'Pune, MH' },
  '422': { lat: 19.9975, lon: 73.7898, name: 'Nashik, MH' },
  '431': { lat: 19.8762, lon: 75.3433, name: 'Chhatrapati Sambhajinagar, MH' },
  '440': { lat: 21.1458, lon: 79.0882, name: 'Nagpur, MH' },
  '444': { lat: 20.9374, lon: 77.7796, name: 'Amravati, MH' },
  '416': { lat: 16.7050, lon: 74.2433, name: 'Kolhapur / Sangli, MH' },
  '403': { lat: 15.2993, lon: 74.1240, name: 'Goa' },
  '40':  { lat: 19.0760, lon: 72.8777, name: 'Mumbai Region, MH' },
  '41':  { lat: 18.5204, lon: 73.8567, name: 'Pune / Western MH' },
  '42':  { lat: 20.0000, lon: 74.0000, name: 'Nashik / North MH' },
  '43':  { lat: 19.8762, lon: 75.3433, name: 'Marathwada, MH' },
  '44':  { lat: 21.1458, lon: 79.0882, name: 'Vidarbha / Nagpur, MH' },

  // Madhya Pradesh & Chhattisgarh (45 - 49)
  '452': { lat: 22.7196, lon: 75.8577, name: 'Indore, MP' },
  '462': { lat: 23.2599, lon: 77.4126, name: 'Bhopal, MP' },
  '474': { lat: 26.2183, lon: 78.1828, name: 'Gwalior, MP' },
  '482': { lat: 23.1815, lon: 79.9864, name: 'Jabalpur, MP' },
  '492': { lat: 21.2514, lon: 81.6296, name: 'Raipur, CG' },
  '490': { lat: 21.1938, lon: 81.3509, name: 'Bhilai / Durg, CG' },
  '45':  { lat: 22.7196, lon: 75.8577, name: 'Malwa / Indore, MP' },
  '46':  { lat: 23.2599, lon: 77.4126, name: 'Bhopal Region, MP' },
  '47':  { lat: 26.2183, lon: 78.1828, name: 'Gwalior / Chambal, MP' },
  '48':  { lat: 23.1815, lon: 79.9864, name: 'Jabalpur / Rewa, MP' },
  '49':  { lat: 21.2514, lon: 81.6296, name: 'Chhattisgarh' },

  // Andhra Pradesh & Telangana (50 - 53)
  '500': { lat: 17.3850, lon: 78.4867, name: 'Hyderabad, TS' },
  '506': { lat: 17.9689, lon: 79.5941, name: 'Warangal, TS' },
  '520': { lat: 16.5062, lon: 80.6480, name: 'Vijayawada, AP' },
  '530': { lat: 17.6868, lon: 83.2185, name: 'Visakhapatnam, AP' },
  '517': { lat: 13.6288, lon: 79.4192, name: 'Tirupati, AP' },
  '50':  { lat: 17.3850, lon: 78.4867, name: 'Telangana' },
  '51':  { lat: 14.4673, lon: 78.8242, name: 'Rayalaseema, AP' },
  '52':  { lat: 16.3067, lon: 80.4365, name: 'Guntur / Vijayawada, AP' },
  '53':  { lat: 17.6868, lon: 83.2185, name: 'Coastal AP / Vizag' },

  // Karnataka (56 - 59)
  '560': { lat: 12.9716, lon: 77.5946, name: 'Bengaluru, KA' },
  '570': { lat: 12.2958, lon: 76.6394, name: 'Mysuru, KA' },
  '575': { lat: 12.9141, lon: 74.8560, name: 'Mangaluru, KA' },
  '580': { lat: 15.3647, lon: 75.1240, name: 'Hubballi / Dharwad, KA' },
  '590': { lat: 15.8497, lon: 74.4977, name: 'Belagavi, KA' },
  '585': { lat: 17.3297, lon: 76.8343, name: 'Kalaburagi, KA' },
  '56':  { lat: 12.9716, lon: 77.5946, name: 'Bengaluru Region, KA' },
  '57':  { lat: 13.0000, lon: 75.5000, name: 'Coastal / Malnad KA' },
  '58':  { lat: 15.3647, lon: 75.1240, name: 'North Karnataka' },
  '59':  { lat: 15.8497, lon: 74.4977, name: 'Belagavi Region, KA' },

  // Tamil Nadu & Puducherry (60 - 64)
  '600': { lat: 13.0827, lon: 80.2707, name: 'Chennai, TN' },
  '641': { lat: 11.0168, lon: 76.9558, name: 'Coimbatore, TN' },
  '625': { lat: 9.9252,  lon: 78.1198, name: 'Madurai, TN' },
  '620': { lat: 10.7905, lon: 78.7047, name: 'Tiruchirappalli, TN' },
  '636': { lat: 11.6643, lon: 78.1460, name: 'Salem, TN' },
  '605': { lat: 11.9416, lon: 79.8083, name: 'Puducherry' },
  '60':  { lat: 13.0827, lon: 80.2707, name: 'Chennai Region, TN' },
  '61':  { lat: 10.7905, lon: 79.1378, name: 'Thanjavur Region, TN' },
  '62':  { lat: 9.9252,  lon: 78.1198, name: 'Madurai / South TN' },
  '63':  { lat: 12.0000, lon: 78.5000, name: 'Vellore / North TN' },
  '64':  { lat: 11.0168, lon: 76.9558, name: 'Coimbatore / West TN' },

  // Kerala & Lakshadweep (67 - 69)
  '682': { lat: 9.9312,  lon: 76.2673, name: 'Kochi / Ernakulam, KL' },
  '695': { lat: 8.5241,  lon: 76.9366, name: 'Thiruvananthapuram, KL' },
  '673': { lat: 11.2588, lon: 75.7804, name: 'Kozhikode, KL' },
  '680': { lat: 10.5276, lon: 76.2144, name: 'Thrissur, KL' },
  '67':  { lat: 11.2588, lon: 75.7804, name: 'Malabar / North Kerala' },
  '68':  { lat: 9.9312,  lon: 76.2673, name: 'Central Kerala' },
  '69':  { lat: 8.5241,  lon: 76.9366, name: 'South Kerala' },

  // West Bengal, Sikkim & Andaman (70 - 74)
  '700': { lat: 22.5726, lon: 88.3639, name: 'Kolkata, WB' },
  '711': { lat: 22.5958, lon: 88.2636, name: 'Howrah, WB' },
  '734': { lat: 26.7271, lon: 88.3953, name: 'Siliguri / North Bengal' },
  '737': { lat: 27.3389, lon: 88.6065, name: 'Gangtok, Sikkim' },
  '744': { lat: 11.6234, lon: 92.7265, name: 'Port Blair, AN' },
  '70':  { lat: 22.5726, lon: 88.3639, name: 'Kolkata Metropolis, WB' },
  '71':  { lat: 22.5958, lon: 88.2636, name: 'Howrah / Hooghly, WB' },
  '72':  { lat: 22.4257, lon: 87.3199, name: 'Midnapore, WB' },
  '73':  { lat: 26.7271, lon: 88.3953, name: 'North Bengal & Sikkim' },
  '74':  { lat: 23.2324, lon: 87.8615, name: 'Bardhaman / Nadia, WB' },

  // Odisha (75 - 77)
  '751': { lat: 20.2961, lon: 85.8245, name: 'Bhubaneswar, OD' },
  '753': { lat: 20.4625, lon: 85.8828, name: 'Cuttack, OD' },
  '769': { lat: 22.2604, lon: 84.8536, name: 'Rourkela, OD' },
  '75':  { lat: 20.2961, lon: 85.8245, name: 'Coastal Odisha' },
  '76':  { lat: 19.3149, lon: 84.7941, name: 'South Odisha / Berhampur' },
  '77':  { lat: 21.4669, lon: 83.9812, name: 'Western Odisha / Sambalpur' },

  // North Eastern States (78 - 79)
  '781': { lat: 26.1445, lon: 91.7362, name: 'Guwahati, AS' },
  '793': { lat: 25.5788, lon: 91.8933, name: 'Shillong, ML' },
  '795': { lat: 24.8170, lon: 93.9368, name: 'Imphal, MN' },
  '797': { lat: 25.6751, lon: 94.1086, name: 'Kohima, NL' },
  '796': { lat: 23.7271, lon: 92.7176, name: 'Aizawl, MZ' },
  '799': { lat: 23.8315, lon: 91.2868, name: 'Agartala, TR' },
  '791': { lat: 27.0844, lon: 93.6053, name: 'Itanagar, AR' },
  '78':  { lat: 26.1445, lon: 91.7362, name: 'Assam' },
  '79':  { lat: 25.5788, lon: 91.8933, name: 'NE States' },

  // Bihar & Jharkhand (80 - 85)
  '800': { lat: 25.5941, lon: 85.1376, name: 'Patna, BR' },
  '834': { lat: 23.3441, lon: 85.3096, name: 'Ranchi, JH' },
  '831': { lat: 22.8046, lon: 86.2029, name: 'Jamshedpur, JH' },
  '826': { lat: 23.7957, lon: 86.4304, name: 'Dhanbad, JH' },
  '842': { lat: 26.1209, lon: 85.3647, name: 'Muzaffarpur, BR' },
  '823': { lat: 24.7955, lon: 85.0002, name: 'Gaya, BR' },
  '80':  { lat: 25.5941, lon: 85.1376, name: 'Patna Region, BR' },
  '81':  { lat: 25.2425, lon: 86.9842, name: 'Bhagalpur, BR' },
  '82':  { lat: 23.7957, lon: 86.4304, name: 'Dhanbad / Bokaro, JH' },
  '83':  { lat: 23.3441, lon: 85.3096, name: 'Ranchi Region, JH' },
  '84':  { lat: 26.1209, lon: 85.3647, name: 'North Bihar' },
  '85':  { lat: 25.7796, lon: 87.4753, name: 'Purnia / Katihar, BR' },
};

/**
 * Get latitude and longitude for any 6-digit Indian pincode.
 * Searches 3-digit prefix first (city/district), then 2-digit prefix (state/region).
 */
export function getPincodeCoordinates(pincode: string | number): Coordinates | null {
  if (!pincode) return null;
  const pinStr = String(pincode).trim().replace(/\D/g, '');
  if (pinStr.length < 2) return null;

  // 1. Try 3-digit prefix (e.g. "560", "400", "263")
  const prefix3 = pinStr.substring(0, 3);
  if (PINCODE_COORDINATES[prefix3]) {
    return PINCODE_COORDINATES[prefix3];
  }

  // 2. Try 2-digit prefix (e.g. "56", "27", "26")
  const prefix2 = pinStr.substring(0, 2);
  if (PINCODE_COORDINATES[prefix2]) {
    return PINCODE_COORDINATES[prefix2];
  }

  return null;
}


export function calculatePincodeDistance(
  fromPincode: string | number,
  toPincode: string | number
): { distanceKm: number; fromName?: string; toName?: string; isEstimated: boolean } {
  const coord1 = getPincodeCoordinates(fromPincode);
  const coord2 = getPincodeCoordinates(toPincode);

  if (!coord1 || !coord2) {
    // If coordinates cannot be determined, fallback to default distance based on state matching
    const p1 = String(fromPincode).slice(0, 2);
    const p2 = String(toPincode).slice(0, 2);
    const isSameState = p1 === p2;
    return {
      distanceKm: isSameState ? 100 : 450,
      isEstimated: true,
    };
  }

  // Same pincode / same locality
  if (String(fromPincode).trim() === String(toPincode).trim()) {
    return {
      distanceKm: 20,
      fromName: coord1.name,
      toName: coord2.name,
      isEstimated: false,
    };
  }

  // Convert degrees to radians
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const lat1 = toRadians(coord1.lat);
  const lon1 = toRadians(coord1.lon);
  const lat2 = toRadians(coord2.lat);
  const lon2 = toRadians(coord2.lon);

  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Aerial distance (Great-circle distance on Earth sphere radius 6371 km)
  const aerialDistance = 6371 * c;

  // Road distance factor: actual road travel distance is ~1.20x to 1.25x of aerial distance in India
  const roadFactor = aerialDistance < 100 ? 1.30 : (aerialDistance < 500 ? 1.25 : 1.20);
  const roadDistance = Math.round(aerialDistance * roadFactor);

  // Minimum 10 km, maximum 4000 km per NIC rule #8
  const finalDistance = Math.min(4000, Math.max(10, roadDistance));

  return {
    distanceKm: finalDistance,
    fromName: coord1.name,
    toName: coord2.name,
    isEstimated: false,
  };
}
